import React, { useCallback, useMemo, useState } from 'react';
import { InputGroup, InputLeftElement, Input, InputRightElement, useColorMode } from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';

import { useCalendarSettings, useFilters } from '../../../../lib/context';
import { useDebounce } from '../../../../lib/hooks';
import { __ } from '../../../../lib/i18n';

interface ISearchFilterProps {
	onApplyFilters?: () => void;
}

export const SearchFilter: React.FC<ISearchFilterProps> = ({ onApplyFilters }) => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const { searchTerm, setSearchTerm } = useFilters();
	const [showClearIcon, setShowClearIcon] = useState(false);
	const { debounced: applyDebouncedFilters, cancel: cancelDebouncedFilters } = useDebounce(onApplyFilters, 500);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setSearchTerm(value);
			setShowClearIcon(value.length > 0);
			applyDebouncedFilters();
		},
		[setSearchTerm, setShowClearIcon, applyDebouncedFilters]
	);

	const clearSearch = useCallback(() => {
		cancelDebouncedFilters();
		setSearchTerm('');
		setShowClearIcon(false);
		if (onApplyFilters) {
			onApplyFilters();
		}
	}, [cancelDebouncedFilters, setSearchTerm, setShowClearIcon, onApplyFilters]);

	const { filtersTextColor, calendarBordersColor } = settings.styles[colorMode];

	const inputSx = useMemo(
		() => ({
			'::placeholder': {
				color: filtersTextColor,
			},
		}),
		[filtersTextColor]
	);

	return (
		<InputGroup>
			<InputLeftElement pointerEvents='none'>
				<SearchIcon color='gray.300' />
			</InputLeftElement>
			<Input
				placeholder={__('Search events')}
				borderRadius='xs'
				fontSize='sm'
				borderColor={calendarBordersColor}
				value={searchTerm}
				onChange={handleSearchChange}
				color={filtersTextColor}
				sx={inputSx}
			/>
			{showClearIcon && (
				<InputRightElement>
					<CloseIcon cursor='pointer' color='gray.500' onClick={clearSearch} />
				</InputRightElement>
			)}
		</InputGroup>
	);
};
